import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { CheckIcon, CopyIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** A one-time password the server just issued; never readable again. */
export interface Secret {
  email: string;
  password: string;
}

interface Props {
  secret: Secret | null;
  onClose: () => void;
}

export function TemporaryPasswordDialog({ secret, onClose }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!secret) return null;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("settings.users.temporaryPasswordTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("settings.users.temporaryPasswordDescription", {
              email: secret.email,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="bg-muted flex items-center gap-2 rounded-lg p-3">
          <code className="grow font-mono text-sm select-all">
            {secret.password}
          </code>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("settings.users.copy")}
            onClick={() =>
              void navigator.clipboard
                .writeText(secret.password)
                .then(() => setCopied(true))
            }
          >
            {copied ? (
              <CheckIcon className="size-4" />
            ) : (
              <CopyIcon className="size-4" />
            )}
          </Button>
        </div>
        {copied && (
          <p className="text-muted-foreground text-sm">
            {t("settings.users.copied")}
          </p>
        )}
        <DialogFooter>
          <Button onClick={onClose}>{t("common.buttons.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
